import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChevronRight, History, Info, RefreshCw } from 'lucide-react';
import runHistoryService from '../../../services/runHistoryService';
import PaginationBar from '../../PlatformAdmin/PaginationBar';
import RunDetailDrawer from '../RunDetail/RunDetailDrawer';
import {
  n0,
  formatCost,
  formatDurationMs,
  getBrowserTz,
  RUN_TYPE_LABELS,
  STATUS_LABELS,
  WINDOW_OPTIONS,
} from '../UsageAnalytics/usageFormat';
import { formatAgo, formatWhen, runStatus, runTitle } from '../RunDetail/runDetailFormat';
import sharedStyles from '../../PlatformAdmin/PlatformAdmin.module.scss';
import styles from '../RunDetail/RunDetail.module.scss';

const PAGE_SIZE = 25;

const RUN_TYPE_OPTIONS = ['all', 'single', 'workflow_main', 'workflow_node', 'team', 'batch_loop'];
const HISTORY_WINDOWS = WINDOW_OPTIONS.filter((o) =>
  ['24h', '7d', '30d', '90d', 'today', 'mtd', 'all'].includes(o.value)
);

/**
 * Run History for the Agent UI — the caller's own runs (`/agent-metrics/runs`,
 * permission `dashboard`), with the Run Details drawer as its leaf.
 *
 * `agentId` locks the list to one agent (the playground's HISTORY tab); without
 * it the panel lists every run the user has triggered (the /run-history page).
 *
 * `top_level_only` defaults to ON: a chain writes one row per node and a HIL
 * team one per member leg, so ON gives one entry per thing the user actually
 * triggered — the nodes/legs show up as sub-runs inside the detail drawer. The
 * toggle exists for the flat debug view.
 */
const RunHistoryPanel = ({ agentId = null, chainId = null, teamId = null, onToast, dense = false }) => {
  const tz = useMemo(() => getBrowserTz(), []);

  const [windowValue, setWindowValue] = useState('30d');
  const [runType, setRunType] = useState('all');
  const [status, setStatus] = useState('all');
  const [topLevelOnly, setTopLevelOnly] = useState(true);
  const [offset, setOffset] = useState(0);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedRun, setSelectedRun] = useState(null); // { runId, hint }

  const params = useMemo(() => {
    const p = { tz, window: windowValue, top_level_only: topLevelOnly, limit: PAGE_SIZE, offset };
    if (agentId) p.agent_id = agentId;
    if (chainId) p.chain_id = chainId;
    if (teamId) p.team_id = teamId;
    if (runType !== 'all') p.run_type = runType;
    if (status !== 'all') p.status = status;
    return p;
  }, [tz, windowValue, topLevelOnly, offset, agentId, chainId, teamId, runType, status]);

  // Any filter change (not pagination) goes back to page 1.
  useEffect(() => {
    setOffset(0);
  }, [windowValue, runType, status, topLevelOnly, agentId, chainId, teamId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await runHistoryService.listRuns(params);
      const items = data?.items || data?.runs || [];
      setRows(items);
      setTotal(data?.total ?? items.length);
    } catch (err) {
      // 403 means the menu shouldn't have been reachable; 500 usually means the
      // run-trace migration hasn't been applied on this environment yet.
      setError(err?.data?.detail || err?.message || 'Failed to load run history.');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const anyWithoutTrace = useMemo(() => rows.some((r) => r.has_trace === false), [rows]);

  // Stable identity: the drawer refetches whenever `fetchDetail` changes, so an
  // inline arrow here would put it in a reload loop.
  const fetchDetail = useCallback((id) => runHistoryService.getRunDetail(id), []);

  const openRun = (run) => {
    if (!run?.run_id || run.has_trace === false) return;
    setSelectedRun({ runId: run.run_id, hint: run });
  };

  return (
    <Box className={styles.historyWrap}>
      <Box className={styles.filtersBar}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Time range</InputLabel>
          <Select label="Time range" value={windowValue} onChange={(e) => setWindowValue(e.target.value)}>
            {HISTORY_WINDOWS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Run type</InputLabel>
          <Select label="Run type" value={runType} onChange={(e) => setRunType(e.target.value)}>
            {RUN_TYPE_OPTIONS.map((v) => (
              <MenuItem key={v} value={v}>
                {RUN_TYPE_LABELS[v] || v}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {Object.keys(STATUS_LABELS).map((v) => (
              <MenuItem key={v} value={v}>
                {STATUS_LABELS[v]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Tooltip
          title="Off shows every recorded row, including the individual chain nodes and team legs nested inside a run"
          arrow
        >
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={topLevelOnly}
                onChange={(e) => setTopLevelOnly(e.target.checked)}
              />
            }
            label={<span style={{ fontSize: 12.5, fontWeight: 600 }}>Top-level runs only</span>}
          />
        </Tooltip>

        <span className={styles.filtersSpacer} />

        <Tooltip title="Refresh" arrow>
          <span>
            <IconButton className={sharedStyles.subtleBtn} onClick={load} disabled={loading}>
              <RefreshCw size={16} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <div className={`${styles.notice} ${styles.noticeError}`}>
          <Info size={15} aria-hidden />
          <span>{error}</span>
          <Button size="small" className={sharedStyles.outlineBtn} onClick={load} sx={{ ml: 1 }}>
            Retry
          </Button>
        </div>
      )}

      {/* Day-one reality: there is no backfill, so runs from before the feature
          shipped (and any whose trace aged out) list without a detail. */}
      {anyWithoutTrace && (
        <div className={styles.notice}>
          <Info size={15} aria-hidden />
          <span>
            Runs marked <strong>No detail</strong> were recorded before run tracing was enabled, or
            their trace passed the 90-day retention window. Their metrics are still accurate.
          </span>
        </div>
      )}

      {loading && rows.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : rows.length === 0 ? (
        <Box className={sharedStyles.emptyState} sx={{ p: dense ? 4 : 8 }}>
          <History className={sharedStyles.emptyIcon} size={44} />
          <Typography variant="h6">No runs yet</Typography>
          <Typography variant="body2">
            {agentId
              ? 'Nothing recorded for this agent in the selected time range.'
              : 'Nothing recorded for you in the selected time range.'}
          </Typography>
        </Box>
      ) : (
        // No scroll region of its own: the page (or the playground's logs
        // column) is the scroll container, so the list is free to grow.
        <Box className={sharedStyles.tableCard}>
          {rows.map((run, idx) => {
            const cost = formatCost(run.estimated_cost, run.estimated_cost != null ? 100 : null);
            const st = runStatus(run);
            const openable = Boolean(run.run_id) && run.has_trace !== false;
            const ago = formatAgo(run.started_at);

            return (
              <Box
                key={run.run_id || run.id || idx}
                className={`${styles.runRow} ${openable ? '' : styles.runRowStatic}`}
                sx={{ flexWrap: 'wrap' }}
                role={openable ? 'button' : undefined}
                tabIndex={openable ? 0 : undefined}
                onClick={() => openRun(run)}
                onKeyDown={(e) => {
                  if (openable && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    openRun(run);
                  }
                }}
              >
                <div className={styles.runRowMain}>
                  <span className={sharedStyles.companyName}>{runTitle(run)}</span>
                  <span className={sharedStyles.companyMeta}>
                    {formatWhen(run.started_at)}
                    {ago ? ` · ${ago}` : ''}
                    {run.parent_run_id ? ' · step of another run' : ''}
                  </span>
                </div>

                <span className={styles.typePill}>
                  {RUN_TYPE_LABELS[run.run_type] || run.run_type || '—'}
                </span>

                <span
                  className={`${sharedStyles.statusPill} ${
                    st === 'error' ? sharedStyles.inactive : sharedStyles.active
                  }`}
                >
                  {st}
                </span>

                <div className={styles.runRowNumbers}>
                  <span className={styles.runMetric}>{n0(run.total_tokens)} tok</span>
                  <span className={sharedStyles.muted}>{formatDurationMs(run.duration_ms)}</span>
                </div>

                <div className={styles.runRowNumbers}>
                  <span className={styles.runMetric}>{cost.text}</span>
                  <span className={sharedStyles.muted}>{n0(run.tool_calls)} tools</span>
                </div>

                {openable ? (
                  <ChevronRight size={16} aria-hidden style={{ flexShrink: 0 }} />
                ) : (
                  <span className={styles.noTraceTag}>No detail</span>
                )}

                {run.error_message && <span className={styles.errorLine}>{run.error_message}</span>}
              </Box>
            );
          })}
        </Box>
      )}

      {total > 0 && (
        <PaginationBar
          total={total}
          limit={PAGE_SIZE}
          offset={offset}
          loading={loading}
          unit="runs"
          onOffsetChange={setOffset}
        />
      )}

      <RunDetailDrawer
        open={Boolean(selectedRun)}
        runId={selectedRun?.runId}
        runHint={selectedRun?.hint}
        fetchDetail={fetchDetail}
        onClose={() => setSelectedRun(null)}
        onToast={onToast}
      />
    </Box>
  );
};

export default RunHistoryPanel;
